import { Context } from "@nuxt/types";
import { inject } from "@nuxtjs/composition-api";

import { isAuth0UserToken } from "@/schemes/auth0";
import { getLikesStore } from "@/store/likes";
import { isIab } from "@/utils/iab-identification";

/**
 * @TODO Convert this to a composable only used in components
 */
export default class LinkService {
  public context!: Context;
  public initialPage = "direct";

  public constructor(context: Context) {
    this.context = context;
  }

  public getSearchParams({
    init,
    page,
    ltkId,
    productSetId,
    abTests = [],
    priceDropPercent,
    lcn,
    lcs,
  }: GetSearchParamsProps): URLSearchParams {
    const baseEventData = this.context.$amplitude?.getBaseEventData();
    const params = new URLSearchParams(init);

    // Client ID for web ltk
    params.set("__cid", "1144");
    // Current locale rendered for the user
    params.set("l", this.context.store.$i18n.locale);
    // Current page, hard coded for now to align with mobile app
    params.set("p", page || baseEventData?.page || "");

    // Click-out referrer logic, see https://rewardstyle.atlassian.net/browse/ACQ-1164 for more information.

    // Determine if the user arrived from an external source
    const hasReferrerSet = Boolean(typeof document !== "undefined" && document?.referrer);
    const isExternal = hasReferrerSet || isIab();

    // Set root referrer (rr): 'external' for external traffic, initial landing page for direct traffic
    // Root referrer should not change throughout the session
    const rootReferrer = isExternal ? "external" : this.initialPage;
    params.set("rr", rootReferrer);

    // Set referrer (r): previous page on shopltk.com if available, otherwise match rootReferrer
    params.set("r", this.context.from?.name ?? rootReferrer);

    if (lcn) {
      params.set("lcn", lcn);
    }

    if (lcs) {
      params.set("lcs", lcs);
    }

    if (this.context.$branch?.vid) {
      params.set("vid", this.context.$branch?.vid);
    }
    // Append user information if the user is logged in
    const userId =
      (isAuth0UserToken(this.context.$auth.user) &&
        this.context.$auth.user["http://shopltk.com/profile"].sigilUserId) ||
      this.context.$auth.user?.userId;
    if (userId) {
      // user id
      params.set("u", `${userId}`);
    }

    const id = ltkId || params.get("li");
    if (id) {
      if (!params.get("li")) {
        params.set("li", id);
      }

      const likesStore = getLikesStore(this.context.store);
      const like = likesStore.likes[id];
      if (like !== undefined) {
        // like type
        params.set("lt", like.likeType);
        // like network
        params.set("ln", like.network);
      }
    }

    const ci = productSetId || params.get("ci");
    if (ci) {
      params.set("ci", ci);
    }

    if (abTests.length) {
      abTests.forEach(([name, value], index) => {
        params.set(`a${index + 1}n`, name);
        params.set(`a${index + 1}v`, value);
      });
    }

    const percentChange = Number(priceDropPercent);
    if (!isNaN(percentChange) && percentChange > 0) {
      params.set("hpd", "true");
      params.set("pdp", percentChange.toString());
    } else {
      params.set("hpd", "false");
    }

    return params;
  }

  public getProductLink({
    url,
    page,
    ltkId,
    productSetId,
    abTests = [],
    priceDropPercent,
  }: GetProductLinkProps): string {
    const newUrl = new URL(url);
    const params = this.getSearchParams({
      init: newUrl.searchParams,
      page,
      ltkId,
      productSetId,
      abTests,
      priceDropPercent,
      lcn: inject("utm_campaign", ""),
      lcs: inject("utm_source", ""),
    });

    newUrl.search = params.toString();

    return newUrl.toString();
  }

  public getImageLink({ url, width, height, useDefaultDpr = true, crop = true }: GetImageLinkProps): string {
    if (!url) {
      return "";
    }

    const newUrl = new URL(url);
    const params = newUrl.searchParams;

    if (!params.get("v")) {
      params.set("v", "0");
    }

    params.set("auto", "format");
    params.set("fm", this.context.$featureDetection?.webp ? "webp" : "jpg");

    if (width) {
      params.set("w", width);
    }

    if (height) {
      params.set("h", height);
    }

    if (crop) {
      params.set("fit", "crop");
    }

    params.set("q", "80");

    if (useDefaultDpr) {
      params.set("dpr", this.context.$featureDetection?.dpr.toString());
    }

    // toString will persist order of params set. Do not rearrange the below params or it will invalidate CDN cache
    newUrl.search = params.toString();

    return newUrl.toString();
  }

  public getUtmParams({ url, webSharePath }: GetUtmParamsProps): string {
    if (!url) {
      return "";
    }

    const newUrl = new URL(url);
    const params = newUrl.searchParams;

    if (!params.get("web_share_path")) {
      params.set("web_share_path", webSharePath);
    }

    newUrl.search = params.toString();

    return newUrl.toString();
  }

  public getProductDetailsLink({
    productDetailsId,
    ltkId,
    productSetId,
    rshash,
    variantId,
  }: GetProductDetailsLinkProps): string {
    if (!productDetailsId) {
      return "";
    }

    const params = new URLSearchParams();
    if (ltkId) {
      params.set("li", ltkId);
    }

    if (productSetId) {
      params.set("ci", productSetId);
    }

    if (rshash) {
      params.set("rshash", rshash);
    }

    if (variantId) {
      params.set("variant", variantId);
    }

    if (this.context.$branch?.vid) {
      params.set("vid", this.context.$branch?.vid);
    }

    return `/products/${productDetailsId}?${params.toString()}`;
  }
}
